import fromPairs from 'lodash/fromPairs';
import uuid from 'utils/id';
import get from 'lodash/get';
import { normalize } from 'normalizr';

import { ENTITIES_KEY_NODE, formNodeSchema } from 'common/entities/formNode';
import {
    NODE_NAME_ADDITIONAL_GUEST_TOP_GROUP,
    NODE_NAME_ADDITIONAL_GUEST_COUNT,
} from 'common/entities/formNode/nodeNames';
import { ENTITIES_KEY_FORM_LOGIC_RULE } from 'common/entities/formLogicRule';

import { getRootNodeId } from 'common/entities/form/selectors';
import { getFormNode } from 'common/entities/formNode/selectors';
import { VALUE_TYPE_REFERENCES } from 'common/entities/formLogicRule/constants';

import { updateFormValue } from 'common/compounds/FormRenderer/actions/form';

import {
    REMOVE_FORM_NODE,
    UPDATE_FORM_NODE,
    UPDATE_FORM_NODE_CONFIG,
    CREATE_FORM_NODES,
} from '../constants/actionTypes';
import {
    getFormId,
    isAdditionalGuestInfoNode,
    getAdditionalGuestInfoNodes,
    getAdditionalGuestGroupNodeIds,
    getEditFormNodeId,
} from '../selectors';
import { getNodeGroupId } from '../selectors/templateLibrary';
import {
    getReferencedAdditionalGuestNumber,
    replaceAdditionalGuestNumber,
} from '../utils/additionalGuestHelperFunctions';
import { openEditFormNode, removeValidationErrors, resetEditingFormNode } from './';
import { EDIT_LOCKED_NODE_NAMES, TOP_BLOCKED_NODES } from '../constants/nodeNameRules';
import { questionTypesWithOptions } from '../utils/formNodeValidations';
import { getFirstSelectedEntityOptionValue, getNodeOptionsWithFirstSelectedOnly } from '../utils/nodeOptionsHelpers';

const LABEL = 'label';
const LABEL_MARKUP = 'labelMarkup';
const TEXT_ELEMENT_DEFAULT_DESCRIPTION = 'This is a new Text Element!';
export const INPUT_TYPE = 'inputType';

/**
 * Util function to apply callbacks when a node is modified
 * This is useful when the node is an additional guest question and
 * we need to replicate the same changes the rest
 * This encapsulates the logic to identify an additional guest question
 *
 * Usage in thunks. Examples:
 *
 *  - When the logic to replicate is the same for additional guest questions:
 *      applyCallbackToNodeWithAdditionalGuestsSupport(state, nodeId)(
            node => dispatch(createActionRemoveFormNode(node.id)),
        );
*
*   - When you need additional logic to additional guest questions:
        applyCallbackToNodeWithAdditionalGuestsSupport(state, nodeId)(
            node => dispatch(createActionAddFormNodeOption(node, label, value)),
            additionalGuestNode => dispatch(createActionAddFormNodeOption(additionalGuestNode, label, value, commonOptionId)),
        );
*
*   - When you need additional conditions, besides if it is additional guest question:
        applyCallbackToNodeWithAdditionalGuestsSupport(state, nodeId)(
            () => dispatch(createActionUpdateFormNode(node, updates)),
            additionalNode => dispatch(createActionUpdateFormNode(additionalNode, updates)),
            () => isNodePropertyThatCarriesToAdditionalGuestNodes(key),
        );
 */
export const applyCallbackToNodeWithAdditionalGuestsSupport =
    (state, nodeId) =>
    (normalNodeCb, optionalAdditionalGuestNodesCb, additionalConditionCb = () => true) => {
        const node = getFormNode(state, nodeId);
        const formId = getFormId(state);
        const additionalGuestNodesCb = optionalAdditionalGuestNodesCb || normalNodeCb;
        const isAdditionalGuest = isAdditionalGuestInfoNode(state, formId, nodeId) && additionalConditionCb();

        if (isAdditionalGuest) {
            const additionalNodesToUpdate = getAdditionalGuestInfoNodes(state, formId, node.crossGroupId);

            additionalNodesToUpdate.forEach(additionalGuestNodesCb);
        } else {
            normalNodeCb(node);
        }
    };

/**
 * Replaces all ids inside the given array of nodes, keeping in sync ids that matched.
 *
 * @param  {Object[]} nodes
 * @param  {Object[]} rules
 * @return {Object} - { formNodesWithNewIds,formRulesWithNewIds }
 */
const generateNewIdsForNodes = (nodes, rules) => {
    const newIds = {};

    const getNewId = (oldId) => {
        if (!newIds[oldId]) {
            newIds[oldId] = `_${uuid()}`;
        }
        return newIds[oldId];
    };

    const formNodesWithNewChildrenIds = nodes.map((node) => ({
        ...node,
        id: getNewId(node.id),
        children: node.children.map(getNewId),
    }));
    const formNodesWithNewIds = formNodesWithNewChildrenIds.map((node) => ({
        ...node,
        parent: !newIds[node.parent] ? node.parent : getNewId(node.parent),
    }));

    const formRulesWithNewIds = rules.map((rule) => ({
        ...rule,
        target: VALUE_TYPE_REFERENCES.indexOf(rule.targetType) > -1 ? getNewId(rule.target) : rule.target,
        operandLeft:
            VALUE_TYPE_REFERENCES.indexOf(rule.operandLeftType) > -1 ? getNewId(rule.operandLeft) : rule.operandLeft,
        operandRight:
            VALUE_TYPE_REFERENCES.indexOf(rule.operandRightType) > -1 ? getNewId(rule.operandRight) : rule.operandRight,
        defaultValue:
            VALUE_TYPE_REFERENCES.indexOf(rule.defaultValueType) > -1 ? getNewId(rule.defaultValue) : rule.defaultValue,
    }));

    return { formNodesWithNewIds, formRulesWithNewIds };
};

/**
 * @param {string} nodeId
 * @return {Object} - action
 */
export const createActionRemoveFormNode = (nodeId) => ({
    type: REMOVE_FORM_NODE,
    id: nodeId,
});

/**
 * Given a denormalized node, normalizes it and replaces given ids for new ones.
 *
 * @param  {Object[]} denormalizedNode
 * @param  {string} parentNodeId
 * @param  {string} formId - id of form that node is being added to.
 * @return {Object}
 *      newFormNodes - new form node entities.
 *      newFormLogicRules - new form logic rule entities.
 *      newNodeId - first node's new id.
 */
export const normalizeAndPrepareForAdding = (denormalizedNodes, parentNodeId, formId) => {
    // Normalize nodes and rules.
    const { formNodes, formRules } = denormalizedNodes.reduce(
        (memo, denormalizedNode) => {
            const entities = normalize(denormalizedNode, formNodeSchema);

            const formNodeEntities = get(entities, ['entities', ENTITIES_KEY_NODE], {});
            const formRuleEntities = get(entities, ['entities', ENTITIES_KEY_FORM_LOGIC_RULE], {});

            // Turn into array. Extract `logic` key.
            const formNodesArray = Object.values(formNodeEntities).map(({ logic, ...node }) => ({ ...node }));
            const formRulesArray = Object.values(formRuleEntities);

            return {
                formNodes: [...memo.formNodes, ...formNodesArray],
                formRules: [...memo.formRules, ...formRulesArray],
            };
        },
        { formNodes: [], formRules: [] },
    );

    // Set parent id on the top level nodes being added.
    const denormalizedNodeIds = fromPairs(denormalizedNodes.map((node) => [node.id, true]));
    const formNodesWithTopParent = formNodes.map((node) => ({
        ...node,
        parent: denormalizedNodeIds[node.id] ? parentNodeId : node.parent,
    }));

    // Generate new temporary IDs.
    const { formNodesWithNewIds, formRulesWithNewIds } = generateNewIdsForNodes(formNodesWithTopParent, formRules);

    // Add formId to all nodes and rules.
    const newFormNodes = formNodesWithNewIds.map((node) => ({
        ...node,
        formId,
    }));
    const newFormLogicRules = formRulesWithNewIds.map((rule) => ({
        ...rule,
        formId,
    }));

    // Replace {{n}} for 0 in added nodes.
    // There's no state of generic enumerable {{n}} in FormBuilder.
    const newFormNodesNonEnumerable = newFormNodes.map((node) => ({
        ...node,
        name: node.name.replace('{{n}}', '0'),
    }));

    return {
        newFormNodes: newFormNodesNonEnumerable,
        newFormLogicRules,
    };
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
// Thunks
// ⬇️⬇️⬇️⬇️⬇️⬇️
// /////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * @param {string} nodeId
 */
export const removeFormNode = (nodeId) => (dispatch, getState) => {
    const state = getState();

    // If the node being edited is the deleted one, we first stop editing it.
    const editFormNodeId = getEditFormNodeId(state);
    if (nodeId === editFormNodeId) {
        dispatch(resetEditingFormNode());
    }

    // Changes in an additional guest field need to be applied to all additional guest groups.
    applyCallbackToNodeWithAdditionalGuestsSupport(
        state,
        nodeId,
    )((node) => {
        const nodeToRemoveId = node.name === NODE_NAME_ADDITIONAL_GUEST_COUNT ? node.parent : node.id;

        dispatch(createActionRemoveFormNode(nodeToRemoveId));
    });

    dispatch(removeValidationErrors());
};

/**
 * @param {Object} node
 * @param {Object} updates
 * @return {Object} - action
 */
export const createActionUpdateFormNode = (node, updates) => ({
    type: UPDATE_FORM_NODE,
    payload: {
        entities: {
            [ENTITIES_KEY_NODE]: {
                [node.id]: {
                    ...node,
                    ...updates,
                    id: node.id,
                },
            },
        },
    },
});

/** @var {Object} - functions that returns extra node updates to apply when changing an entity */
const updatesToApplyByKey = {
    inputType: (state, node, value) => {
        if (!questionTypesWithOptions.includes(value)) {
            return {};
        }

        const newNodeOptions = getNodeOptionsWithFirstSelectedOnly(node);
        return {
            config: {
                ...(node.config || {}),
                options: newNodeOptions,
            },
        };
    },
};

/**
 * @param {string} nodeOd
 * @param {string} key
 * @param {*} value
 */
export const updateFormNodeValue = (nodeId, key, value) => (dispatch, getState) => {
    const state = getState();
    const node = getFormNode(state, nodeId);

    const extraUpdates = updatesToApplyByKey[key] ? updatesToApplyByKey[key](state, node, value) : {};

    const updates = {
        ...{ [key]: value },
        ...extraUpdates,
    };

    const isInputType = key === INPUT_TYPE;
    const entitiesFromUpdates =
        isInputType && questionTypesWithOptions.includes(value) ? get(updates, 'config.options.entities', {}) : {};
    const firstSelectedEntityValue = getFirstSelectedEntityOptionValue(entitiesFromUpdates);

    // Changes in an additional guest field need to be applied to all additional guest groups.
    applyCallbackToNodeWithAdditionalGuestsSupport(state, nodeId)(
        () => dispatch(createActionUpdateFormNode(node, updates)),
        (additionalNode) => {
            dispatch(createActionUpdateFormNode(additionalNode, updates));
            // Clears the value when changing the question input type for additional quest
            isInputType && dispatch(updateFormValue(additionalNode.id, firstSelectedEntityValue));
        },
    );

    // Clears the value when changing the question input type
    isInputType && dispatch(updateFormValue(nodeId, firstSelectedEntityValue));
};

export const updateFormNodeLabel = (nodeId, label, labelMarkup) => (dispatch, getState) => {
    const state = getState();
    const node = getFormNode(state, nodeId);

    const updates = {
        [LABEL]: label,
        [LABEL_MARKUP]: labelMarkup,
    };

    // Changes in an additional guest field need to be applied to all additional guest groups.
    applyCallbackToNodeWithAdditionalGuestsSupport(state, nodeId)(
        () => dispatch(createActionUpdateFormNode(node, updates)),
        (additionalNode) => {
            dispatch(createActionUpdateFormNode(additionalNode, updates));
        },
    );
};

/**
 * @param {Object} node
 * @param {string} key
 * @param {mixed} value
 * @return {Object} - action
 */
export const createActionUpdateFormNodeConfigValue = (node, key, value) => ({
    type: UPDATE_FORM_NODE_CONFIG,
    id: node.id,
    updates: { [key]: value },
});

export const updateFormNodeConfigValue = (nodeId, key, value) => (dispatch, getState) => {
    const state = getState();

    // Changes in an additional guest field need to be applied to all additional guest groups.
    applyCallbackToNodeWithAdditionalGuestsSupport(state, nodeId)(
        (additionalNode) => dispatch(createActionUpdateFormNodeConfigValue(additionalNode, key, value)),
        (node) => dispatch(createActionUpdateFormNodeConfigValue(node, key, value)),
    );
};

export const openEditViewLastCreatedNode = () => (dispatch, getState) => {
    const state = getState();
    const nodeIds = Object.keys(state.entities[ENTITIES_KEY_NODE]);
    const lastCreatedNodeId = nodeIds[nodeIds.length - 1];
    const lastCreatedNode = getFormNode(state, lastCreatedNodeId);

    if (EDIT_LOCKED_NODE_NAMES.includes(lastCreatedNode.name)) {
        return;
    }

    dispatch(openEditFormNode(lastCreatedNodeId));
};

/**
 * @param {Object[]} denormalizedNodes
 * @param {string} [parentNodeId] - by default, the root node
 * @param {number} [position] - by default, after last child.
 */
export const createFormNodes =
    (denormalizedNodes, parentNodeId = null, position = null) =>
    (dispatch, getState) => {
        const state = getState();
        const formId = getFormId(state);
        const rootNodeId = getRootNodeId(state, formId);
        const nodeName = get(denormalizedNodes, '0.name');
        const fixedPosition = TOP_BLOCKED_NODES.indexOf(nodeName);

        // Changes in an additional guest field need to be applied to all additional guest groups.
        applyCallbackToNodeWithAdditionalGuestsSupport(state, parentNodeId || rootNodeId)(
            (node) =>
                dispatch({
                    type: CREATE_FORM_NODES,
                    denormalizedNodes,
                    parentNodeId: node.id,
                    rootNodeId,
                    position: fixedPosition > -1 ? fixedPosition : position,
                }),
            (additionalNode) => {
                const guestNumber = getReferencedAdditionalGuestNumber(additionalNode);
                const denormalizedNodesWithUpdatedNames = denormalizedNodes.map((node) =>
                    replaceAdditionalGuestNumber(node, guestNumber),
                );
                dispatch({
                    type: CREATE_FORM_NODES,
                    denormalizedNodes: denormalizedNodesWithUpdatedNames,
                    parentNodeId: additionalNode.id,
                    rootNodeId,
                    position,
                });
            },
        );
    };

/**
 * @param {Object} template
 * @return {Object[]}
 */
const getFormNodeInTemplate = (template) => {
    if (!template.shouldUnwrap) {
        return [template.data];
    }

    // If the wrapper node had logic, we need to put it in a child so that it's not discarded.
    const children = [...template.data.children];
    if (template.data.logic) {
        children[0] = {
            ...children[0],
            logic: template.data.logic,
        };
    }
    return children;
};

const isNewAdditionalGuestTopGroup = (nodeTemplate) => nodeTemplate.name === NODE_NAME_ADDITIONAL_GUEST_TOP_GROUP;

const addCrossGroupIdToNode =
    (getNewId = uuid) =>
    (node) => ({ ...node, crossGroupId: `additional_guest-${getNewId()}` });

const addCrossGroupIdToAdditionalGuestNodes = (denormalizedNodes, getNewId) =>
    denormalizedNodes.map((template) =>
        isNewAdditionalGuestTopGroup(template)
            ? {
                  ...template,
                  children: template.children.map((additionalGuestGroup) =>
                      additionalGuestGroup.children
                          ? {
                                ...additionalGuestGroup,
                                children: additionalGuestGroup.children.map(addCrossGroupIdToNode(getNewId)),
                            }
                          : additionalGuestGroup,
                  ),
              }
            : template,
    );

/**
 * @param {Object} template - in serialized tree shape.
 * @param {string} [parentNodeId] - passed when adding to a group, null to add at bottom of form.
 * @return {Function}
 */
export const createFormNodeFromTemplate =
    (template, parentNodeId = null) =>
    (dispatch, getState, { getNewId } = {}) => {
        const state = getState();
        const formId = getFormId(state);

        // Transform serialized tree of nodes into list of denormalized nodes to add.
        const denormalizedNodes = getFormNodeInTemplate(template);

        // This is the ID of the group where the question would be added to, or null for root.
        const groupId = parentNodeId !== null ? parentNodeId : getNodeGroupId(state);
        const group = groupId ? getFormNode(state, groupId) : null;

        // If adding to a bringing guest group, we have more work.
        if (group && isNewAdditionalGuestTopGroup(group)) {
            const denormalizedNodesWithCrossGroupId = denormalizedNodes.map(addCrossGroupIdToNode(getNewId));
            const additionalGuestGroupIds = getAdditionalGuestGroupNodeIds(state, formId);
            additionalGuestGroupIds.forEach((nodeId) =>
                dispatch(createFormNodes(denormalizedNodesWithCrossGroupId, nodeId)),
            );
        } else {
            // If any of the nodes we're adding is the Bringing Guests group, then we need to add the
            // crossGroupId to all applicable nodes (Guest First Name, Guest Email, etc.)
            const denormalizedNodesWithCrossGroupId = denormalizedNodes.some(isNewAdditionalGuestTopGroup)
                ? addCrossGroupIdToAdditionalGuestNodes(denormalizedNodes, getNewId)
                : denormalizedNodes;
            const parentId = groupId || getRootNodeId(state, formId);

            dispatch(createFormNodes(denormalizedNodesWithCrossGroupId, parentId));
        }

        if (denormalizedNodes.length === 1) {
            dispatch(openEditViewLastCreatedNode());
        }
    };

/**
 * @param {Object} denormalizedNode
 * @param {string} [parentNodeId] - by default, the root node
 * @param {number} [position] - by default, after last child.
 * @return {Function}
 */
export const createFormNode = (denormalizedNode, parentNodeId = null, position = null) =>
    createFormNodes([denormalizedNode], parentNodeId, position);

/**
 * @param {string} [parentNodeId] - passed when adding to a group, null to add at bottom of form.
 * @return {Function}
 */
export const createTextElementContentNode =
    (parentNodeId = null) =>
    (dispatch) => {
        const textElementQuestion = {
            data: {
                id: '_EMPTY_TEXT_ELEMENT_',
                type: 'content',
                description: TEXT_ELEMENT_DEFAULT_DESCRIPTION,
            },
        };

        dispatch(createFormNodeFromTemplate(textElementQuestion, parentNodeId));
    };
